import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { CheckCircleIcon, EnvelopeIcon } from '../../utils/IconWrapper';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalCard = styled(motion.div)`
  background: var(--bg-secondary);
  border-radius: 16px;
  padding: 2.5rem;
  max-width: 500px;
  width: 100%;
  text-align: center;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  border: 1px solid var(--border-color);
`;

const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  font-size: 2rem;
  color: white;
`;

const Title = styled.h2`
  font-size: 1.8rem;
  color: #0d9488;
  margin-bottom: 1rem;
`;

const Description = styled.p`
  color: var(--text-secondary);
  margin-bottom: 2rem;
  font-size: 1.1rem;
  line-height: 1.6;
`;

const EmailHighlight = styled.div`
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  padding: 1.5rem;
  border-radius: 12px;
  margin: 1.5rem 0;
  color: white;
  text-align: center;
`;

const EmailTitle = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const EmailText = styled.p`
  margin: 0;
  font-size: 1rem;
  line-height: 1.5;
`;

const InstructionsList = styled.div`
  background: var(--bg-primary);
  padding: 1.5rem;
  border-radius: 8px;
  margin: 1rem 0;
  text-align: left;
`;

const InstructionItem = styled.div`
  margin: 0.5rem 0;
  color: var(--text-secondary);
  font-size: 0.9rem;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
`;

const ActionButton = styled.button`
  background: linear-gradient(135deg, #0d9488 0%, #14b8a6 100%);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(13, 148, 136, 0.4);
  }
`;

interface EmailConfirmationModalProps {
  userEmail?: string;
  onClose: () => void;
  onResendEmail?: () => void;
}

const EmailConfirmationModal: React.FC<EmailConfirmationModalProps> = ({
  userEmail,
  onClose,
  onResendEmail
}) => {
  return (
    <ModalOverlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <ModalCard
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        <SuccessIcon>
          <CheckCircleIcon />
        </SuccessIcon>
        
        <Title>Account creato con successo! 🎉</Title>
        
        <Description>
          Benvenuto nella famiglia J-O-H! Il tuo account è stato registrato correttamente.
        </Description>
        
        <EmailHighlight>
          <EmailTitle>
            <EnvelopeIcon />
            Conferma la tua email
          </EmailTitle>
          <EmailText>
            <strong>Passo importante:</strong> Ti abbiamo inviato un'email di conferma{userEmail && ` a ${userEmail}`}.<br />
            Clicca sul link nell'email per attivare il tuo account.
          </EmailText>
        </EmailHighlight>

        <InstructionsList>
          <h4 style={{ margin: '0 0 1rem 0', color: 'var(--text-primary)' }}>
            💡 Cosa fare ora:
          </h4>
          <InstructionItem>
            <span>📧</span>
            <span>Controlla la tua casella di posta (anche spam/promozioni)</span>
          </InstructionItem>
          <InstructionItem>
            <span>🔗</span>
            <span>Clicca sul link di conferma nell'email</span>
          </InstructionItem>
          <InstructionItem>
            <span>🏠</span>
            <span>Inizia a esplorare i nostri alloggi</span>
          </InstructionItem>
          <InstructionItem>
            <span>📄</span>
            <span>Completa la verifica documenti quando vuoi dal profilo</span>
          </InstructionItem>
        </InstructionsList>
        
        <ActionButton onClick={onClose}>
          Ho capito, inizia a esplorare
        </ActionButton>
        
        {onResendEmail && (
          <button
            onClick={onResendEmail}
            style={{
              background: 'none',
              border: 'none',
              color: '#0d9488',
              textDecoration: 'underline',
              cursor: 'pointer',
              margin: '1rem 0 0 0',
              fontSize: '0.9rem'
            }}
          >
            Non hai ricevuto l'email? Invia di nuovo
          </button>
        )}
      </ModalCard>
    </ModalOverlay>
  );
};

export default EmailConfirmationModal; 